import React, { useState, useRef, useEffect } from "react";
import TextInput from "../components/ui/TextInput";
import UploadFileCard from "../components/ui/UploadFileCard";
import toast from "../lib/utils/toast";
import {
  uploadFiles,
  generateAudio,
  getContentHistory,
} from "../lib/redux/actions";
import InfiniteScroll from "react-infinite-scroll-component";
import classNames from "classnames";
import daburLogo from "../assets/svg/appIcon.svg";
import Avatar from "../assets/svg/avatar.svg";

import RotatingTextLoader from "../lib/common/components/loader/RotatingTextLoader";
import { useOutletContext } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { trackEvent, trackException, trackTrace } from "../lib/utils/telemetryService";
import { MESSAGES } from "../config/messages";
import PPTSkeletonLoader from "../components/presentation/PPTSkeletonLoader";

export default function GenerateAudio() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [chats, setChats] = useState([]);
  const scrollRef = useRef(null);
  const [page, setPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const isFetchingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  const scrollHeightRef = useRef(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isChatLoading, setIsChatLoading] = useState(true);

  const { isMobile } = useOutletContext();

  useEffect(() => {
    handleGetContentHistory(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetContentHistory = async (pageNumber = 1, isInitialLoad = false) => {
    if (isFetchingRef.current) return;

    try {
      if (isInitialLoad) setIsChatLoading(true);
      isFetchingRef.current = true;
      setIsLoadingMore(true);
      if (pageNumber === 1) setIsLoadingHistory(true);
      trackEvent("fetchChatHistory_GenerateAudio_Start", { pageNumber });
      // Store current scroll height before loading new data
      if (scrollRef.current) {
        scrollHeightRef.current = scrollRef.current.scrollHeight;
      }

      const data = {
        page: pageNumber,
        limit: 4,
        chatType: 4, // AUDIO type
      };
      const res = await getContentHistory(data);
      const newChats = res.data.data || [];

      // Filter out duplicate entries (where questionId is null)
      const filteredChats = newChats.filter((chat) => chat.questionId !== null);

      const existingIds = new Set(chats.map((chat) => chat._id));
      const uniqueNewChats = filteredChats.filter(
        (chat) => !existingIds.has(chat._id)
      );

      const processedMessages = uniqueNewChats.flatMap((chat) => [
        {
          role: "user",
          message: chat.questionId.message,
          _id: chat.questionId._id,
          type: "text",
        },
        {
          role: "assistant",
          message: chat.content,
          _id: chat._id,
          type: "audio",
        },
      ]);

      setChats((prev) => [...processedMessages, ...prev]);
      setHasMore(processedMessages.length > 0);

      // After state update, adjust scroll position
       trackEvent("fetchChatHistory_GenerateAudio_Success", {
              pageNumber,
              fetched: processedMessages?.length,
            }); // [TRACKING LINE: 74]
      trackTrace(`Fetched ${processedMessages?.length} audio chat(s)`);
      
      // setTimeout(() => {
      //   if (scrollRef.current) {
      //     const newScrollHeight = scrollRef.current.scrollHeight;
      //     const scrollDiff = newScrollHeight - scrollHeightRef.current;
      //     scrollRef.current.scrollTop = scrollDiff;
      //   }
      // }, 0);
    } catch (error) {
      console.error(error);
      trackException(error);
      trackEvent("fetchChatHistory_GenerateAudio_Failed"); 
    } finally {
      isFetchingRef.current = false;
      setIsLoadingMore(false);
      if (isInitialLoad) setIsChatLoading(false);
      if (pageNumber === 1) setIsLoadingHistory(false);
      
    }
  };

  const handleGenerate = async ({ text, media = [] } = {}) => {
  
    const userPrompt = text || prompt;
    trackEvent("GenerateAudio_Attempt", { promptLength: (userPrompt || "").length }); // [TRACKING LINE: 108]
    trackTrace(`GenerateAudio payload prompt: ${userPrompt?.slice(0, 200)}`);
    setChats((prev) => [
      ...prev,
      { role: "user", message: userPrompt, type: "text" },
      { role: "assistant", message: "Generating audio...", type: "loading" },
    ]);
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }, 100);
    setLoading(true);
    const payload = {
      prompt: userPrompt,
    };
    try {
      const res = await generateAudio(payload);
      const { data } = res;

      setTimeout(() => {
        setChats((prev) => {
          const updated = [...prev];
          const lastIndex = updated
            .map((c) => c.role + c.type)
            .lastIndexOf("assistantloading");
          if (lastIndex !== -1) {
            updated[lastIndex] = {
              role: "assistant",
              message: data.output_blob_url, // Using content instead of output_blob_url
              type: "audio",
              _id: data._id,
            };
          }
          return updated;
        });
        setLoading(false);
      }, 2000);
       trackEvent("GenerateAudio_Success", { audioId: data?._id });
    } catch (error) {
      setChats((prev) => {
        const updated = [...prev];
        const lastIndex = updated
          .map((c) => c.role + c.type)
          .lastIndexOf("assistantloading");
        if (lastIndex !== -1) {
          updated[lastIndex] = {
            role: "assistant",
            message:
              "I can not generate an Audio from the prompt you have given",
            type: "error",
          };
        }
        return updated;
      });
      setLoading(false);
      trackException(error); 
      trackEvent("GenerateAudio_Failed");
    }
  };

  const fetchMoreData = async () => {
    if (isFetchingRef.current || !hasMore || isLoadingMore) return;

    // Only fetch more if we're near the top
    if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    setIsLoadingMore(true);

    // Set a new timeout
    scrollTimeoutRef.current = setTimeout(async () => {
      const nextPage = page + 1;
      setPage(nextPage);
      await handleGetContentHistory(nextPage);
    }, 300); // 300ms debounce
  };

  const handleFileChange = async (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const allowedTypes = [
      "audio/mpeg",
      "audio/wav",
      "audio/ogg",
      "audio/mp4",
      "audio/webm",
    ];

    const MAX_FILE_SIZE_MB = 10;
    const validFiles = Array.from(selectedFiles).filter((file) => {
      const isValidType = allowedTypes.includes(file.type);
      const isValidSize = file.size <= MAX_FILE_SIZE_MB * 1024 * 1024;

      if (!isValidType) {
        toast.error(`Unsupported format: ${file.name}`);
        return false;
      }

      if (!isValidSize) {
        toast.error(`File size is too big (Maximum allowed size is 10MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;
    trackEvent("GenerateAudio_FileUpload_Start", { fileCount: validFiles.length });
    try {
      setIsUploading(true);
      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      const result = await uploadFiles(formData);

      if (result.statusCode === 200) {
         trackEvent("GenerateAudio_FileUpload_Success", { uploaded: result.data.length });
        setFiles(
          result.data.map((url, index) => ({
            fileUrl: url,
            fileName: validFiles[index].name,
          }))
        );
      }
    } catch (error) {
      trackException(error); 
      trackEvent("GenerateAudio_FileUpload_Failed");
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const showSampleGrid = !isLoadingHistory && chats.length === 0;

  // Add scroll to bottom function
  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  if (isChatLoading) {
    return <PPTSkeletonLoader />;
  }

  return (
    <div
      className={`flex-1 flex flex-col mt-4 justify-between overflow-scroll mx-4`}
    >
      {showSampleGrid && (
        <div
          className={`flex flex-col justify-between w-full px-4 sm:px-6 md:px-8 lg:px-12 sm:w-[90vw] md:w-[80vw] lg:w-[60vw] xl:w-[40vw] my-16 sm:my-12 md:my-16 lg:my-20 xl:my-24 mx-auto h-full ${
            isMobile ? "text-left" : "text-center"
          }`}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl mb-3 sm:mb-4">
            Generate Audio or Hear an Explanation
          </h2>
          <h4 className="text-base sm:text-lg md:text-xl lg:text-2xl mb-4 sm:mb-5 md:mb-6">
            Convert your text into a natural-sounding audio clip or let Dabur
            GPT explain any topic in a clear, spoken format.
          </h4>
          <h5 className="border bg-white p-2 sm:p-3 md:p-4 rounded-lg text-left text-xs sm:text-sm md:text-base text-[#1b5c29]">
            "Voiceover for Dabur Gulabari product ad"
            <br />
            "Benefits of Chyawanprash for immunity"
            <br />
            "How Dabur ensures Ayurvedic purity"
          </h5>
        </div>
      )}

      {!showSampleGrid && (
        <div
          id="scrollableDiv"
          ref={scrollRef}
          className={`flex-1 py sm:px-[50px] md:px-[20px] lg:px-[100px]  overflow-y-auto  xl:!px-[220px] 2xl:!px-[220px] flex flex-col-reverse mt-[40px] ${
            !isMobile && " !px-[30px]"
          }`}
        >
          <button
            onClick={scrollToBottom}
            className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
          <InfiniteScroll
            inverse={true}
            dataLength={chats.length}
            next={fetchMoreData}
            hasMore={hasMore}
            scrollableTarget="scrollableDiv"
            loader={null}
            endMessage={
              chats?.totalPages >= 2 ? (
                <p className="text-center m-2 font-segoeUI">No More chats!</p>
              ) : (
                ""
              )
            }
            scrollThreshold="200px"
            className="!overflow-hidden"
            // style={{ overflow: "hidden" }}
          >
            {chats.map((chat, idx) => (
              <div
                key={chat?._id}
                className={`flex mb-4 gap-4 w-full ${chat.role === "user" ? "justify-end" : "justify-start"}`}

              >
                {chat.role !== "user" && !isMobile && (
                  <div className="w-[40px] align-self-center flex-shrink-0 my-auto">
                    <img
                      src={daburLogo}
                      alt="Dabur Logo"
                      className="w-[40px]"
                    />
                  </div>
                )}
                <div
                  className={`text-[#000000] flex flex-col gap-2 overflow-x-auto min-h-[44px] justify-center w-fit group ${
                    chat.role === "user"
                      ? "bg-[#1b5c2926] rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px]"
                      : ""
                  }`}
                >
                  {chat.type === "text" && (
                    <span className="p-2">{chat.message}</span>
                  )}
                  {chat.type === "loading" && (
                    <span className="p-2">
                      <RotatingTextLoader messages={[
                        "Interpreting audio context",
                        "Synthesizing sound frequencies",
                        "Generating audio waveform",
                        "Polishing audio fidelity",
                        "Finalizing generation"
                      ]} />
                    </span>
                  )}
                  {chat.type === "error" && (
                    <span className="text-red-500 p-2">{chat.message}</span>
                  )}
                  {chat.type === "audio" && (
                    <div
                      // style={{ position: "relative", display: "inline-block" }}
                      className="!relative !inline-block"
                    >
                      <audio
                        controls
                        className={`${isMobile ? "max-w-[80vw]" : "w-[400px]"}`}
                      >
                        <source src={chat.message} type="audio/mpeg" />
                        <track kind="captions" label="No captions available" src="" />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
                {chat.role === "user" && !isMobile && (
                  <div className="!w-[35px] flex-shrink-0">
                    <img src={Avatar} alt="User Avatar" className="w-[40px]" />
                  </div>
                )}
              </div>
            ))}
          </InfiniteScroll>

          {isLoadingMore && !isLoadingHistory && (
            <div className="flex justify-center items-center gap-2 my-4 py-2 w-full">
              <svg
                className="animate-spin"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#1B5C29"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              <span className="text-sm text-[#1B5C29] font-medium">Loading more...</span>
            </div>
          )}
        </div>
      )}
      <div
        className={classNames(
          `sticky shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bg-[#ECF2EE] bottom-0 mb-0 py-4  sm:!px-[150px] md:!px-[20px] lg:!px-[200px] xl:!px-[200px] 2xl:!px-[200px] ${
            !isMobile && " !px-[30px]"
          }`
        )}
      >
        <div>
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {files.map((file, index) => (
                <UploadFileCard
                  key={`uploadfilecard-${file?.fileUrl}}`}
                  file={file}
                  index={index}
                  removeFile={removeFile}
                />
              ))}
            </div>
          )}
        </div>
        <div className="relative w-full !h-[72px] rounded-[14px] !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]">
          <TextInput
            inputRef={inputRef}
            placeholder="Describe your audio script"
            inputText={prompt}
            handleChatResponse={handleGenerate}
            setInputText={setPrompt}
            inputFocused={inputFocused}
            setInputFocused={setInputFocused}
            loading={loading}
            disabled={loading && chats.length > 0}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            isUploading={isUploading}
            files={files}
            setFiles={setFiles}
            chats={chats}
            isFileUploadOptionVisible={false}
            isContentRendering={loading}
          />
        </div>
        <div className="max-w-4xl mx-auto mt-[40px]">
          <p className="text-xs text-gray-500 text-center">
          {MESSAGES.POWERED_BY}
          </p>
        </div>
      </div>
    </div>
  );
}
